/**
 * @file useListingFilters.js
 * @brief Provides asynchronous filter loaders for locations and categories.
 */
import { useCallback, useRef } from "react";

function normalizeSearchValue(value = "") {
    return value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
}

function buildCategoryOption(category) {
    const pathLabel = category.full_path || category.name;

    return {
        value: category.id,
        label: category.name,
        pathLabel,
        childCount: category.child_count || 0,
        searchText: normalizeSearchValue([
            category.name,
            category.parent_name,
            category.full_path,
            category.slug,
        ].filter(Boolean).join(" ")),
    };
}

/**
 * Build async select loaders backed by the listing filter endpoints.
 * @param {Object} api API client used for filter requests.
 * @returns {{loadLocations: Function, loadCategories: Function, loadCategoryOptionsByParent: Function}} Async option loaders for listing filters.
 */
export default function useListingFilters(api) {
    const categoriesCacheRef = useRef(null);
    const categoriesByParentRef = useRef(new Map());

    const loadLocations = useCallback(
        async (inputValue) => {
            try {
                const res = await api.get("locations/", { params: { search: inputValue } });
                return res.data.results.map((loc) => ({ value: loc.id, label: loc.name }));
            } catch (err) {
                console.error(err);
                return [];
            }
        },
        [api]
    );

    const loadCategories = useCallback(
        async (inputValue) => {
            try {
                if (!categoriesCacheRef.current) {
                    const allCategories = [];
                    let page = 1;
                    let hasNextPage = true;

                    while (hasNextPage) {
                        const res = await api.get("categories/", { params: { page } });
                        const results = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
                        allCategories.push(...results.map(buildCategoryOption));

                        hasNextPage = Boolean(!Array.isArray(res.data) && res.data.next);
                        page += 1;
                    }

                    categoriesCacheRef.current = allCategories.sort((left, right) =>
                        left.pathLabel.localeCompare(right.pathLabel)
                    );
                }

                const normalizedInput = normalizeSearchValue(inputValue);
                if (!normalizedInput) {
                    return categoriesCacheRef.current.slice(0, 50);
                }

                return categoriesCacheRef.current
                    .filter((category) => category.searchText.includes(normalizedInput))
                    .slice(0, 50);
            } catch (err) {
                console.error(err);
                return [];
            }
        },
        [api]
    );

    const loadCategoryOptionsByParent = useCallback(
        async (parentId) => {
            const cacheKey = parentId || "root";
            const cached = categoriesByParentRef.current.get(cacheKey);
            if (cached) {
                return cached;
            }

            const options = [];
            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const res = await api.get("categories/", {
                    params: { page, parent: parentId || "root" },
                });
                const results = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
                options.push(...results.map(buildCategoryOption));

                hasNextPage = Boolean(!Array.isArray(res.data) && res.data.next);
                page += 1;
            }

            options.sort((left, right) => left.label.localeCompare(right.label));
            categoriesByParentRef.current.set(cacheKey, options);
            return options;
        },
        [api]
    );

    return { loadLocations, loadCategories, loadCategoryOptionsByParent };
}
